// client/src/coms/views/canvas_parts/CanvasGamePanel.js
import React, { useEffect, useMemo, useRef, useState } from 'react';

function buttonStyle({ danger = false, primary = false, disabled = false, wide = false } = {}) {
  return {
    minHeight: 30,
    borderRadius: 7,
    border: danger ? '1px solid rgba(248,113,113,0.32)' : primary ? '1px solid rgba(96,165,250,0.34)' : '1px solid rgba(255,255,255,0.12)',
    background: disabled ? 'rgba(255,255,255,0.025)' : danger ? 'rgba(127,29,29,0.25)' : primary ? 'rgba(37,99,235,0.30)' : 'rgba(255,255,255,0.07)',
    color: disabled ? 'rgba(255,255,255,0.34)' : 'rgba(245,247,250,0.96)',
    padding: '0 10px',
    fontSize: 12,
    fontWeight: 850,
    cursor: disabled ? 'default' : 'pointer',
    whiteSpace: 'nowrap',
    flex: wide ? '1 1 auto' : '0 0 auto',
    transition: 'background 120ms ease, border-color 120ms ease',
  };
}

function fieldStyle() {
  return {
    width: '100%',
    minHeight: 30,
    borderRadius: 7,
    border: '1px solid rgba(255,255,255,0.12)',
    background: 'rgba(0,0,0,0.22)',
    color: 'rgba(245,247,250,0.96)',
    padding: '0 8px',
    fontSize: 12,
    fontWeight: 750,
    boxSizing: 'border-box',
  };
}

function labelStyle() {
  return {
    display: 'block',
    marginBottom: 4,
    color: 'rgba(255,255,255,0.56)',
    fontSize: 10,
    fontWeight: 900,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  };
}

function cardStyle(tone = 'neutral') {
  const tones = {
    neutral: ['rgba(255,255,255,0.11)', 'rgba(0,0,0,0.20)'],
    blue: ['rgba(96,165,250,0.34)', 'rgba(30,64,175,0.24)'],
    green: ['rgba(74,222,128,0.28)', 'rgba(20,83,45,0.28)'],
    amber: ['rgba(251,191,36,0.32)', 'rgba(120,53,15,0.28)'],
    red: ['rgba(248,113,113,0.32)', 'rgba(127,29,29,0.25)'],
  };
  const [border, background] = tones[tone] || tones.neutral;
  return { borderRadius: 8, border: `1px solid ${border}`, background, padding: 9 };
}

function formatCategoryLabel(value) {
  if (value === 'number_one_usa_since_1960') return 'US No. 1 hits since 1960';
  if (value === 'all_movies') return 'All movies';
  return String(value || '')
    .replace(/^movies_/, '')
    .replace(/^songs_/, '')
    .replace(/_/g, ' ')
    .replace(/\bsci fi\b/i, 'Sci-fi')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatAnswer(answer) {
  if (!answer) return '';
  if (answer.kind === 'song') return answer.artist ? `${answer.title} — ${answer.artist}` : answer.title;
  return answer.year ? `${answer.title} (${answer.year})` : answer.title;
}

function initials(name) {
  const parts = String(name || '?').trim().split(/\s+/).filter(Boolean);
  return (parts.length > 1 ? parts[0][0] + parts[1][0] : String(name || '?').slice(0, 2)).toUpperCase();
}

function useCountdown(endsAt, serverNow) {
  const [now, setNow] = useState(() => serverNow());
  useEffect(() => {
    if (!endsAt) return undefined;
    setNow(serverNow());
    const timer = window.setInterval(() => setNow(serverNow()), 250);
    return () => window.clearInterval(timer);
  }, [endsAt, serverNow]);
  return endsAt ? Math.max(0, endsAt - now) : 0;
}

function PlayerAvatar({ name, active }) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: 22,
        height: 22,
        borderRadius: '50%',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 9,
        fontWeight: 950,
        background: active ? 'rgba(59,130,246,0.55)' : 'rgba(255,255,255,0.12)',
        color: 'rgba(245,247,250,0.96)',
        flex: '0 0 auto',
      }}
    >
      {initials(name)}
    </span>
  );
}

function PlayerList({ state, currentUserId }) {
  const turn = state.turn;
  const guessed = new Set(turn ? turn.guessedUserIds : []);
  const showScores = state.status !== 'lobby';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }} data-testid="sketch-relay-players">
      {state.players.map((player) => {
        const drawing = turn && turn.drawerUserId === player.userId;
        const offline = !player.connected;
        return (
          <div
            key={player.userId}
            data-testid={`sketch-relay-player-${player.userId}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              minHeight: 30,
              borderRadius: 7,
              padding: '0 7px',
              background: drawing ? 'rgba(59,130,246,0.18)' : guessed.has(player.userId) ? 'rgba(34,197,94,0.14)' : 'rgba(255,255,255,0.045)',
              opacity: offline ? 0.5 : 1,
              transition: 'background 200ms ease, opacity 200ms ease',
            }}
          >
            <PlayerAvatar name={player.name} active={drawing} />
            <span style={{ flex: '1 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12, fontWeight: drawing ? 950 : 800 }}>
              {player.name}
              {player.userId === currentUserId ? <span style={{ opacity: 0.55 }}> (you)</span> : null}
            </span>
            {drawing ? <span title="Drawing" style={{ fontSize: 11 }}>✏️</span> : null}
            {guessed.has(player.userId) ? <span title="Guessed it" style={{ color: 'rgb(134,239,172)', fontSize: 12, fontWeight: 950 }}>✓</span> : null}
            {offline ? <span style={{ fontSize: 10, opacity: 0.8 }}>{player.away ? 'away' : 'reconnecting'}</span> : null}
            {showScores ? <span style={{ fontSize: 12, fontWeight: 950, minWidth: 26, textAlign: 'right' }}>{player.score}</span> : null}
          </div>
        );
      })}
    </div>
  );
}

function GameFeed({ feed }) {
  const listRef = useRef(null);
  useEffect(() => {
    const node = listRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [feed.length]);
  if (!feed.length) return null;
  const colours = { correct: 'rgb(134,239,172)', reveal: 'rgb(253,224,71)', system: 'rgba(191,219,254,0.85)' };
  return (
    <div
      ref={listRef}
      data-testid="sketch-relay-feed"
      style={{ maxHeight: 150, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 3, fontSize: 12, lineHeight: 1.3 }}
    >
      {feed.slice(-25).map((entry) => (
        <div key={entry.id} style={{ color: colours[entry.kind] || 'rgba(245,247,250,0.88)', fontWeight: entry.kind === 'guess' ? 700 : 850, overflowWrap: 'anywhere' }}>
          {entry.kind === 'guess' ? <span style={{ opacity: 0.6 }}>{entry.name}: </span> : null}
          {entry.text}
        </div>
      ))}
    </div>
  );
}

function GuessForm({ game, turnId, disabled }) {
  const [text, setText] = useState('');
  const pending = game.pendingAction === 'guess';
  const submit = async (event) => {
    event.preventDefault();
    const guess = text.trim();
    if (!guess || disabled || pending) return;
    const result = await game.act('guess', { text: guess, turnId });
    if (result && result.ok) setText('');
  };
  return (
    <form onSubmit={submit} style={{ display: 'flex', gap: 6 }} data-canvas-no-swipe="true">
      <input
        value={text}
        onChange={(event) => setText(event.target.value)}
        maxLength={80}
        placeholder={disabled ? 'Waiting for the next drawing…' : 'Type your guess'}
        disabled={disabled}
        aria-label="Your guess"
        autoComplete="off"
        style={{ ...fieldStyle(), minHeight: 34, fontSize: 13 }}
      />
      <button type="submit" disabled={disabled || pending || !text.trim()} style={buttonStyle({ primary: true, disabled: disabled || pending || !text.trim() })}>
        Guess
      </button>
    </form>
  );
}

function TimerBar({ remainingMs, totalMs }) {
  const fraction = totalMs > 0 ? Math.max(0, Math.min(1, remainingMs / totalMs)) : 0;
  const urgent = remainingMs <= 10000;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: '1 1 auto', height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.1)', overflow: 'hidden' }}>
        <div
          style={{
            width: `${fraction * 100}%`,
            height: '100%',
            background: urgent ? 'rgb(248,113,113)' : 'rgb(96,165,250)',
            transition: 'width 250ms linear, background 300ms ease',
          }}
        />
      </div>
      <span data-testid="sketch-relay-timer" style={{ fontSize: 13, fontWeight: 950, minWidth: 30, textAlign: 'right', color: urgent ? 'rgb(252,165,165)' : undefined }}>
        {Math.ceil(remainingMs / 1000)}s
      </span>
    </div>
  );
}

function LobbyOptions({ state, game, canControl }) {
  const options = state.options;
  const categories = (state.categories && state.categories[options.type]) || [];
  const limits = state.limits || {};
  const configure = (patch) => game.act('configure', patch);
  if (!canControl) {
    return (
      <div style={{ color: 'rgba(255,255,255,0.62)', fontSize: 12, lineHeight: 1.35 }}>
        {formatCategoryLabel(options.category)} · {options.rounds} round{options.rounds === 1 ? '' : 's'} · {options.roundSeconds}s per drawing
      </div>
    );
  }
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
      <label>
        <span style={labelStyle()}>Type</span>
        <select value={options.type} onChange={(event) => configure({ type: event.target.value })} style={fieldStyle()}>
          <option value="movies">Movies</option>
          <option value="songs">Songs</option>
        </select>
      </label>
      <label>
        <span style={labelStyle()}>Seconds</span>
        <select value={String(options.roundSeconds)} onChange={(event) => configure({ roundSeconds: Number(event.target.value) })} style={fieldStyle()}>
          {(limits.roundSeconds || [options.roundSeconds]).map((value) => <option key={value} value={value}>{value}</option>)}
        </select>
      </label>
      <label>
        <span style={labelStyle()}>Category</span>
        <select value={options.category} onChange={(event) => configure({ category: event.target.value })} style={fieldStyle()}>
          {categories.map((item) => <option key={item} value={item}>{formatCategoryLabel(item)}</option>)}
        </select>
      </label>
      <label>
        <span style={labelStyle()}>Rounds</span>
        <select value={String(options.rounds)} onChange={(event) => configure({ rounds: Number(event.target.value) })} style={fieldStyle()}>
          {(limits.rounds || [options.rounds]).map((value) => <option key={value} value={value}>{value}</option>)}
        </select>
      </label>
    </div>
  );
}

// The Activities invitation code for this canvas game: anyone with it joins the canvas and its game.
function InviteCode({ canvasId }) {
  const [code, setCode] = useState('');
  useEffect(() => {
    if (!canvasId) return undefined;
    let cancelled = false;
    fetch('/api/activities/canvas-game', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ canvasId }) })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (!cancelled && data && data.code) setCode(String(data.code));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [canvasId]);
  if (!code) return null;
  const link = `${window.location.origin}/comms?gameCode=${code}`;
  return (
    <div data-testid="canvas-game-code" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '6px 8px', borderRadius: 7, background: 'rgba(255,255,255,0.05)', fontSize: 12 }}>
      <span style={{ opacity: 0.7 }}>Invite code</span>
      <strong data-testid="party-code" style={{ letterSpacing: 2, fontSize: 14 }}>{code}</strong>
      <button type="button" style={buttonStyle()} onClick={() => navigator.clipboard && navigator.clipboard.writeText(link).catch(() => {})}>Copy link</button>
    </div>
  );
}

export default function CanvasGamePanel({ game, currentUserId, onClose, canvasId = 0 }) {
  const [open, setOpen] = useState(true);
  const state = game.state;
  const me = useMemo(() => (state ? state.players.find((player) => player.userId === currentUserId) || null : null), [currentUserId, state]);
  const turn = state && state.status === 'drawing' ? state.turn : null;
  const reveal = state && state.status === 'reveal' ? state.reveal : null;
  const turnRemaining = useCountdown(turn ? turn.endsAt : 0, game.serverNow);
  const revealRemaining = useCountdown(reveal ? reveal.endsAt : 0, game.serverNow);
  const canControl = Boolean(me && me.isController) || Boolean(state && state.ownerUserId === currentUserId);
  const isDrawer = Boolean(turn && turn.drawerUserId === currentUserId);
  const prompt = game.privateInfo.prompt && turn && game.privateInfo.prompt.turnId === turn.id ? game.privateInfo.prompt : null;
  const drawer = turn ? state.players.find((player) => player.userId === turn.drawerUserId) : null;
  const iGuessed = Boolean(turn && turn.guessedUserIds.includes(currentUserId));
  const connectedPlayers = state ? state.players.filter((player) => player.connected).length : 0;
  const minPlayers = (state && state.limits && state.limits.minPlayers) || 2;
  const busy = Boolean(game.pendingAction);

  let statusLabel = 'Lobby';
  if (turn) statusLabel = `Round ${state.round}/${state.totalRounds}`;
  else if (reveal) statusLabel = 'Reveal';
  else if (state && state.status === 'finished') statusLabel = 'Game over';

  return (
    <section
      data-testid="sketch-relay-panel"
      data-status={state ? state.status : 'loading'}
      style={{
        borderRadius: 10,
        border: '1px solid rgba(255,255,255,0.12)',
        background: 'rgba(27,31,39,0.92)',
        color: 'rgba(245,247,250,0.96)',
        overflow: 'hidden',
        boxShadow: '0 12px 32px rgba(0,0,0,0.28)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'stretch', background: 'rgba(255,255,255,0.06)' }}>
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          style={{
            flex: '1 1 auto',
            minHeight: 34,
            border: 0,
            background: 'transparent',
            color: 'rgba(245,247,250,0.98)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            padding: '0 10px',
            fontSize: 12,
            fontWeight: 950,
            cursor: 'pointer',
          }}
        >
          <span>Classic draw &amp; guess</span>
          <span style={{ opacity: 0.68, fontWeight: 850 }}>{statusLabel} {open ? '−' : '+'}</span>
        </button>
        {onClose && (!state || !state.players.length) ? (
          <button type="button" onClick={onClose} aria-label="Close classic draw and guess" style={{ border: 0, background: 'transparent', color: 'rgba(255,255,255,0.6)', padding: '0 10px', cursor: 'pointer', fontSize: 14 }}>×</button>
        ) : null}
      </div>

      {open ? (
        <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 9 }}>
          {!state ? (
            <div style={{ color: 'rgba(255,255,255,0.62)', fontSize: 12 }}>{game.loadError || 'Loading game…'}</div>
          ) : null}

          {game.feedback ? (
            <div
              role="status"
              data-testid="sketch-relay-feedback"
              style={{ ...cardStyle(game.feedback.kind === 'error' ? 'red' : game.feedback.kind === 'close' ? 'amber' : 'green'), fontSize: 12, fontWeight: 900 }}
            >
              {game.feedback.text}
            </div>
          ) : null}

          {state && state.status === 'lobby' ? (
            <>
              <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, lineHeight: 1.35 }}>
                Take turns drawing a secret movie or song title while everyone else races to guess it.
              </div>
              <InviteCode canvasId={canvasId} />
              <LobbyOptions state={state} game={game} canControl={canControl} />
              {state.players.length ? <PlayerList state={state} currentUserId={currentUserId} /> : null}
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {me ? (
                  <button type="button" disabled={busy} onClick={() => game.act('leave')} style={buttonStyle({ disabled: busy })}>Leave</button>
                ) : (
                  <button type="button" disabled={busy} onClick={() => game.act('join')} style={buttonStyle({ primary: true, disabled: busy, wide: true })}>Join game</button>
                )}
                {canControl && me ? (
                  <button
                    type="button"
                    disabled={busy || connectedPlayers < minPlayers}
                    onClick={() => game.act('start')}
                    style={buttonStyle({ primary: true, disabled: busy || connectedPlayers < minPlayers, wide: true })}
                  >
                    Start game
                  </button>
                ) : null}
              </div>
              {connectedPlayers < minPlayers ? (
                <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 11 }}>
                  Waiting for players ({connectedPlayers}/{minPlayers}). Invite people to this canvas, then they can join here.
                </div>
              ) : !canControl && me ? (
                <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 11 }}>Waiting for the canvas owner to start.</div>
              ) : null}
            </>
          ) : null}

          {turn ? (
            <>
              <div style={cardStyle()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                  <span style={labelStyle()}>{isDrawer ? 'You are drawing' : 'Drawing now'}</span>
                  <span style={{ fontSize: 10, opacity: 0.6, fontWeight: 900, textTransform: 'uppercase' }}>{turn.kind === 'song' ? 'Song' : 'Movie'}</span>
                </div>
                <div style={{ fontSize: 14, fontWeight: 950, marginBottom: 6 }}>{drawer ? drawer.name : 'Someone'}</div>
                <TimerBar remainingMs={turnRemaining} totalMs={turn.endsAt - turn.startedAt} />
              </div>

              {isDrawer ? (
                <div style={cardStyle('green')} data-testid="sketch-relay-prompt">
                  <div style={{ color: 'rgba(187,247,208,0.78)', fontSize: 10, fontWeight: 950, textTransform: 'uppercase', letterSpacing: 0.5 }}>Draw this — no letters or numbers!</div>
                  <div style={{ marginTop: 3, fontSize: 16, fontWeight: 1000, lineHeight: 1.18 }}>{prompt ? formatAnswer(prompt) : 'Loading your prompt…'}</div>
                </div>
              ) : (
                <div
                  data-testid="sketch-relay-hint"
                  aria-label={`Hint: ${turn.hint.replace(/_/g, 'blank ')}`}
                  style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontSize: 16, fontWeight: 900, letterSpacing: 3, lineHeight: 1.4, overflowWrap: 'anywhere', textAlign: 'center' }}
                >
                  {turn.hint}
                </div>
              )}

              {me && !isDrawer && me.inGame !== false ? (
                iGuessed ? (
                  <div style={{ ...cardStyle('green'), fontSize: 12, fontWeight: 900 }}>You got it! Waiting for the others…</div>
                ) : (
                  <GuessForm game={game} turnId={turn.id} disabled={false} />
                )
              ) : null}

              <GameFeed feed={state.feed} />
              <PlayerList state={state} currentUserId={currentUserId} />
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {isDrawer || canControl ? (
                  <button type="button" disabled={busy} onClick={() => game.act('skip', { turnId: turn.id })} style={buttonStyle({ disabled: busy })}>Skip turn</button>
                ) : null}
                {canControl ? (
                  <button type="button" disabled={busy} onClick={() => game.act('end')} style={buttonStyle({ danger: true, disabled: busy })}>End game</button>
                ) : null}
                {me ? (
                  <button type="button" disabled={busy} onClick={() => game.act('leave')} style={buttonStyle({ disabled: busy })}>Leave</button>
                ) : (
                  <button type="button" disabled={busy} onClick={() => game.act('join')} style={buttonStyle({ primary: true, disabled: busy })}>Join game</button>
                )}
              </div>
            </>
          ) : null}

          {reveal ? (
            <>
              <div style={cardStyle('amber')} data-testid="sketch-relay-reveal">
                <div style={labelStyle()}>The answer was</div>
                <div style={{ fontSize: 16, fontWeight: 1000, lineHeight: 1.2 }}>{formatAnswer(reveal.answer)}</div>
                <div style={{ marginTop: 6, fontSize: 11, opacity: 0.75 }}>
                  {reveal.guessed.length
                    ? `${reveal.guessed.length} correct guess${reveal.guessed.length === 1 ? '' : 'es'} · drawer +${reveal.drawerPoints}`
                    : 'Nobody guessed it.'}
                  {' · '}next in {Math.ceil(revealRemaining / 1000)}s
                </div>
              </div>
              <GameFeed feed={state.feed} />
              <PlayerList state={state} currentUserId={currentUserId} />
              {canControl ? (
                <div style={{ display: 'flex', gap: 6 }}>
                  <button type="button" disabled={busy} onClick={() => game.act('end')} style={buttonStyle({ danger: true, disabled: busy })}>End game</button>
                </div>
              ) : null}
            </>
          ) : null}

          {state && state.status === 'finished' && state.result ? (
            <>
              <div style={cardStyle('blue')} data-testid="sketch-relay-result">
                <div style={labelStyle()}>
                  {state.result.reason === 'not_enough_players' ? 'Game over — not enough players' : state.result.reason === 'ended_by_owner' ? 'Game ended early' : 'Final scores'}
                </div>
                {state.result.standings.map((entry) => (
                  <div key={entry.userId} style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 26, fontSize: 13, fontWeight: entry.rank === 1 ? 1000 : 800 }}>
                    <span style={{ width: 22, textAlign: 'center' }}>{entry.rank === 1 ? '🏆' : `#${entry.rank}`}</span>
                    <span style={{ flex: '1 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {entry.name}{entry.userId === currentUserId ? ' (you)' : ''}
                    </span>
                    <span>{entry.score}</span>
                  </div>
                ))}
              </div>
              <GameFeed feed={state.feed.filter((entry) => entry.kind === 'system').slice(-2)} />
              {state.players.length ? (
                <>
                  <div style={labelStyle()}>Players for the next game</div>
                  <PlayerList state={state} currentUserId={currentUserId} />
                </>
              ) : null}
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {canControl && me ? (
                  <>
                    <button
                      type="button"
                      disabled={busy || connectedPlayers < minPlayers}
                      onClick={() => game.act('rematch')}
                      style={buttonStyle({ primary: true, disabled: busy || connectedPlayers < minPlayers, wide: true })}
                    >
                      Rematch
                    </button>
                    <button type="button" disabled={busy} onClick={() => game.act('lobby')} style={buttonStyle({ disabled: busy })}>Change settings</button>
                  </>
                ) : null}
                {me ? (
                  <button type="button" disabled={busy} onClick={() => game.act('leave')} style={buttonStyle({ disabled: busy })}>Leave</button>
                ) : (
                  <button type="button" disabled={busy} onClick={() => game.act('join')} style={buttonStyle({ primary: true, disabled: busy })}>Join next game</button>
                )}
              </div>
              {!canControl && me ? <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 11 }}>Waiting for the canvas owner to start a rematch.</div> : null}
            </>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
